<template>
  <component :is="isPublicReport ? 'div' : 'panel-navigation'" :class="{ 'workspace-rank-tracker': !isPublicReport }">
    <div class="pa-4 workspace-rank-content" v-show="!loading">
      <v-row class="mb-2 workspace-rank-header">
        <v-col cols="12" class="d-flex justify-space-between align-center">

          <div v-if="isPublicReport && (agency_logo || agency_name)" class="report-agency-brand">
            <img
              v-if="agency_logo"
              :src="agency_logo"
              :alt="agency_name || 'Agency logo'"
              class="report-agency-logo"
            >
            <span v-if="agency_name" class="report-agency-name">
              {{ agency_name }}
            </span>
          </div>

          <div v-if="!isPublicReport">
            <h2 class="text-h5 font-weight-bold grey--text text--darken-3">
              Geo Rank Tracker
            </h2>
            <p class="text-caption grey--text mb-0">
              Local Search Visibility Grid & Service Area Rankings
            </p>
          </div>

          <div v-else>
            <h2 class="text-h5 font-weight-bold grey--text text--darken-3">
              Geo Rank Tracker Report
            </h2>
            <p class="text-caption grey--text mb-0">
              For <strong>{{ company_name }}</strong>
              by <strong>{{ agency_name }}</strong>
            </p>
          </div>

          <div>
            <v-tooltip bottom color="#434C5E" v-if="!isPublicReport">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="success"
                  class="mr-2"
                  @click="addAreaDialog = true"
                >
                  <v-icon left>mdi-map-marker-plus</v-icon>
                  Add Service Areas
                </v-btn>
              </template>
              <span>
                Open the form to add and immediately search new service areas.
              </span>
            </v-tooltip>

            <v-tooltip bottom color="#434C5E">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  class="mr-2"
                  @click="generateRealTimeRankings"
                  :loading="generatingRankings"
                  :disabled="loading"
                >
                  <v-icon left>mdi-chart-timeline-variant</v-icon>
                  Generate Real Time Rankings
                </v-btn>
              </template>
              <span>
                Run the ranking report now without changing the scheduled audit timestamp.
              </span>
            </v-tooltip>

            <v-tooltip bottom color="#434C5E" v-if="!isPublicReport">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="info"
                  @click="emailReportDialog = true"
                >
                  <v-icon left>mdi-email</v-icon>
                  Email Report
                </v-btn>
              </template>
              <span>
                Send a public link to this rank tracker report by email.
              </span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>

      <!-- Snapshot & Ranking Disclaimer Banner -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-alert
            dense
            outlined
            color="blue-grey darken-1"
            class="mb-0 bg-disclaimer rounded-lg"
            icon="mdi-information-outline"
          >
            <div class="d-flex flex-column flex-sm-row align-start align-sm-center">
              <span class="text-caption grey--text text--darken-3">
                <strong>Ranking Snapshot Notice:</strong>
                Local rankings are dynamic point-in-time snapshots that fluctuate
                based on searcher location, time, and active competition.
                Multi-audit trends and expanding green coverage reflect true
                long-term visibility growth.
              </span>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- KPI Metric Cards -->
      <v-row class="mb-4">

        <!-- Best Main Location -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            outlined
            class="pa-4 rounded-lg light-green lighten-5 border-success"
          >
            <div class="text-caption font-weight-bold grey--text text--darken-2">
              BEST RANK
            </div>

            <div class="text-h4 font-weight-black success--text mt-1">
              {{ rankingStats.bestRank ? '#' + rankingStats.bestRank : 'N/A' }}
            </div>

            <div class="text-caption grey--text">
              {{ rankingStats.bestDate || 'No runs' }}
            </div>

            <div
              class="text-caption grey--text text--darken-1 mt-1"
            >
              Main Location
            </div>
          </v-card>
        </v-col>

        <!-- Worst Main Location -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            outlined
            class="pa-4 rounded-lg red lighten-5 border-error"
          >
            <div class="text-caption font-weight-bold grey--text text--darken-2">
              LOWEST RANK
            </div>

            <div class="text-h4 font-weight-black error--text mt-1">
              {{ rankingStats.worstRank ? '#' + rankingStats.worstRank : 'N/A' }}
            </div>

            <div class="text-caption grey--text">
              {{ rankingStats.worstDate || 'No runs' }}
            </div>

            <div
              class="text-caption grey--text text--darken-1 mt-1"
            >
              Main Location
            </div>
          </v-card>
        </v-col>

        <!-- NEW: Average Rank Based On Closest Points -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            outlined
            class="pa-4 rounded-lg blue-grey lighten-5 average-nearest-card"
          >
            <div class="text-caption font-weight-bold grey--text text--darken-2">
              AVERAGE RANK — 5 CLOSEST POINTS
            </div>

            <div
              class="text-h4 font-weight-black blue-grey--text text--darken-3 mt-1"
            >
              {{
                rankingStats.closestPointsAverageRank !== null
                  ? '#' + rankingStats.closestPointsAverageRank
                  : 'N/A'
              }}
            </div>

            <div class="text-caption grey--text">
              {{ rankingStats.closestPointsCount }} of 5 ranked points
            </div>

            <div
              class="text-caption grey--text text--darken-1 mt-1"
            >
              Nearest to main location
            </div>
          </v-card>
        </v-col>

        <!-- Service Areas -->
        <v-col cols="12" sm="6" md="3">
          <v-card
            outlined
            class="pa-4 rounded-lg light-blue lighten-5"
          >
            <div class="text-caption font-weight-bold grey--text text--darken-2">
              SERVICE AREAS TRACKED
            </div>

            <div
              class="text-h4 font-weight-black light-blue--text text--darken-3 mt-1"
            >
              {{ gridPins.length }} Areas
            </div>

            <div class="text-caption grey--text">
              30-day automated scan
            </div>

            <div
              class="text-caption grey--text text--darken-1 mt-1"
            >
              Total Areas
            </div>
          </v-card>
        </v-col>

      </v-row>

      <!-- Explanation of the new average -->
      <v-row
        v-if="rankingStats.closestPoints.length"
        class="mb-4"
      >
        <v-col cols="12">
          <v-alert
            dense
            text
            outlined
            color="blue-grey"
            icon="mdi-map-marker-distance"
            class="mb-0"
          >
            <div class="text-caption">
              <strong>Closest-point average:</strong>
              The average rank above uses the
              {{ rankingStats.closestPoints.length }}
              geographically closest tracked points to the main business location
              for the selected search.
              Points without a ranking are not included in the calculation.
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Map & Grid View -->
      <v-card class="mb-6 pa-4" tile>
        <div class="d-flex justify-space-between align-center mb-3">
          <div class="d-flex align-center gap-2">

            <div class="text-subtitle-1 font-weight-bold">
              Service Area Heatmap Grid
            </div>

            <v-tooltip
              bottom
              color="#434C5E"
              v-if="currentSearchIndex >= 0 && mainSearches[currentSearchIndex]"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  class="grey--text"
                >
                  {{ formatDate(mainSearches[currentSearchIndex].created_at) }}
                </v-chip>
              </template>
              <span>Date of the selected search</span>
            </v-tooltip>

          </div>

          <div class="d-flex align-center gap-2">

            <v-tooltip
              bottom
              color="#434C5E"
              v-if="currentSearchIndex > 0"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  @click="previousSearch"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
              </template>
              <span>View previous search</span>
            </v-tooltip>

            <span
              class="text-caption grey--text"
              v-if="currentSearchIndex >= 0"
            >
              {{ currentSearchIndex + 1 }} of {{ mainSearches.length }}
            </span>

            <v-tooltip
              bottom
              color="#434C5E"
              v-if="
                currentSearchIndex >= 0 &&
                currentSearchIndex < mainSearches.length - 1
              "
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  small
                  @click="nextSearch"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </template>
              <span>View next search</span>
            </v-tooltip>

            <span class="text-caption grey--text">
              <v-icon x-small color="grey">
                mdi-clock-outline
              </v-icon>
              Point-in-time spatial search simulation
            </span>

          </div>
        </div>

        <!-- Map Legend -->
        <div class="map-legend mb-3">

          <!-- Icon Legend -->
          <div class="map-legend-icons">

            <div class="map-legend-section-title">
              Map Legend
            </div>

            <div class="map-legend-items">

              <!-- Main HQ -->
              <div class="map-legend-item">

                <svg
                  class="map-legend-marker marker-hq"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 1L21.5 6.5V17.5L12 23L2.5 17.5V6.5L12 1Z"
                  />
                </svg>

                <div>
                  <div class="map-legend-label">
                    Main Location
                  </div>

                  <div class="map-legend-description">
                    Primary business location
                  </div>
                </div>

              </div>

              <!-- City -->
              <div class="map-legend-item">

                <svg
                  class="map-legend-marker marker-city"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
                  />
                </svg>

                <div>
                  <div class="map-legend-label">
                    City / Area
                  </div>

                  <div class="map-legend-description">
                    Service area location
                  </div>
                </div>

              </div>

              <!-- ZIP -->
              <div class="map-legend-item">

                <svg
                  class="map-legend-marker marker-zip"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M12 1L23 12L12 23L1 12Z"
                  />
                </svg>

                <div>
                  <div class="map-legend-label">
                    ZIP Code
                  </div>

                  <div class="map-legend-description">
                    Tracked ZIP code
                  </div>
                </div>

              </div>

            </div>
          </div>

          <!-- Ranking Color Legend -->
          <div class="map-legend-ranking">

            <div class="map-legend-ranking-header">

              <div>
                <div class="map-legend-section-title">
                  Local Ranking Position
                </div>

                <div class="map-legend-ranking-subtitle">
                  Greener = stronger visibility
                </div>
              </div>

              <div class="map-legend-ranking-note">
                <v-icon x-small color="grey darken-1">
                  mdi-information-outline
                </v-icon>

                Rankings are point-in-time snapshots
              </div>

            </div>

            <div class="rank-gradient-wrapper">

              <div
                class="rank-gradient"
                :style="{ background: rankGradient }"
              ></div>

              <div class="rank-gradient-labels">
                <span>#1</span>
                <span>#5</span>
                <span>#10</span>
                <span>#15</span>
                <span>#20+</span>
              </div>

            </div>

            <div class="rank-gradient-description">
              <span>Best visibility</span>
              <span>Lower visibility</span>
            </div>

          </div>

        </div>

        <div
          id="map"
          style="width: 100%; height: 450px; border-radius: 8px;"
        ></div>
      </v-card>

      <!-- Main Location Audit History Table -->
      <v-card class="pa-4" tile>

        <div class="text-subtitle-1 font-weight-bold mb-3">
          Main Search Position Logs (7-Day Check)
        </div>

        <table class="w-full rg-datatable">

          <thead>
            <tr class="text-left border-b">
              <th class="py-2">TIMESTAMP</th>
              <th class="py-2">RANK</th>
              <th class="py-2">VISIBLE</th>
              <th class="py-2">TOTAL RESULTS</th>
              <th class="py-2 text-right">ACTIONS</th>
            </tr>
          </thead>

          <tbody>

            <tr
              v-for="(item, index) in mainSearches"
              :key="index"
              class="border-b"
            >

              <td class="py-3">
                {{ formatDate(item.created_at) }}
              </td>

              <td class="py-3">

                <v-chip
                  small
                  :color="getRankColor(item.rank)"
                  text-color="white"
                  class="font-weight-bold"
                >
                  {{ item.rank ? '#' + item.rank : '20+' }}
                </v-chip>

              </td>

              <td class="py-3">

                <v-icon
                  small
                  :color="item.has_my_business ? 'success' : 'error'"
                >
                  {{
                    item.has_my_business
                      ? 'mdi-check-circle'
                      : 'mdi-close-circle'
                  }}
                </v-icon>

              </td>

              <td class="py-3">
                {{ item.total_results }}
              </td>

              <td class="py-3 text-right">

                <v-tooltip
                  v-if="item.screenshot_url"
                  left
                  color="#434C5E"
                >
                  <template v-slot:activator="{ on, attrs }">

                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      text
                      color="info"
                      @click="viewScreenshot(item.screenshot_url)"
                      class="mr-2"
                    >
                      <v-icon small>
                        mdi-image
                      </v-icon>
                    </v-btn>

                  </template>

                  <span>
                    View search results screenshot
                  </span>
                </v-tooltip>

                <v-tooltip left color="#434C5E">
                  <template v-slot:activator="{ on, attrs }">

                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      small
                      text
                      color="primary"
                      @click="scrollToSearchOnMap(index)"
                    >
                      Show on Map
                    </v-btn>

                  </template>

                  <span>
                    Scroll to map and highlight this search.
                  </span>
                </v-tooltip>

              </td>

            </tr>

          </tbody>

        </table>
      </v-card>

      <!-- Add Service Areas Modal Dialog -->
      <v-dialog
        v-model="addAreaDialog"
        max-width="550px"
      >

        <v-card>

          <v-card-title class="headline font-weight-bold">
            Add Custom Service Areas & Zip Codes
          </v-card-title>

          <v-card-text class="pt-3">

            <p class="text-body-2 grey--text text--darken-1 mb-2">
              Enter cities, neighborhoods, or 5-digit Zip Codes below.
              Separate multiple locations with commas or new lines.
            </p>

            <v-textarea
              v-model="newLocationsInput"
              outlined
              rows="4"
              placeholder="e.g. 33409, 33411, Delray Beach, Palm Beach Gardens"
              hint="Example: 33401, Boca Raton, 33431"
              persistent-hint
            ></v-textarea>

          </v-card-text>

          <v-card-actions class="pa-4">

            <v-spacer></v-spacer>

            <v-tooltip bottom color="#434C5E">

              <template v-slot:activator="{ on, attrs }">

                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  text
                  @click="addAreaDialog = false"
                  :disabled="submittingAreas"
                >
                  Cancel
                </v-btn>

              </template>

              <span>
                Close this form without adding service areas.
              </span>

            </v-tooltip>

            <v-tooltip bottom color="#434C5E">

              <template v-slot:activator="{ on, attrs }">

                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="success"
                  @click="submitNewServiceAreas"
                  :loading="submittingAreas"
                  :disabled="!newLocationsInput.trim()"
                >
                  Save & Run Report
                </v-btn>

              </template>

              <span>
                Add the entered service areas and run their first ranking search.
              </span>

            </v-tooltip>

          </v-card-actions>

        </v-card>
      </v-dialog>

      <!-- Email Report Dialog -->
      <v-dialog
        v-model="emailReportDialog"
        max-width="550px"
      >

        <v-card>

          <v-card-title class="headline font-weight-bold">
            Email Rank Tracker Report
          </v-card-title>

          <v-card-text class="pt-3">

            <p class="text-body-2 grey--text text--darken-1 mb-3">
              Send the report to the customer via email.
              A public link will be generated that allows the recipient
              to view the report without logging in.
            </p>

            <v-text-field
              v-model="reportEmail"
              label="Email address"
              type="email"
              outlined
              :disabled="sendingReport"
            ></v-text-field>

          </v-card-text>

          <v-card-actions class="pa-4">

            <v-spacer></v-spacer>

            <v-tooltip bottom color="#434C5E">

              <template v-slot:activator="{ on, attrs }">

                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  text
                  @click="emailReportDialog = false"
                  :disabled="sendingReport"
                >
                  Cancel
                </v-btn>

              </template>

              <span>
                Close this dialog without sending the report.
              </span>

            </v-tooltip>

            <v-tooltip bottom color="#434C5E">

              <template v-slot:activator="{ on, attrs }">

                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="info"
                  @click="sendReportEmail"
                  :loading="sendingReport"
                  :disabled="!reportEmail"
                >
                  Send Report
                </v-btn>

              </template>

              <span>
                Email a public link to the address entered above.
              </span>

            </v-tooltip>

          </v-card-actions>

        </v-card>
      </v-dialog>

      <!-- Search Detail Modal Dialog -->
      <v-dialog
        v-model="detailsDialog"
        max-width="700px"
      >

        <v-card v-if="selectedSearch">

          <v-card-title class="headline font-weight-bold">
            Search Results: {{ selectedSearch.search_query }}
          </v-card-title>

          <v-card-text class="pt-3">

            <v-list dense>

              <v-list-item
                v-for="(res, idx) in selectedSearch.results_json"
                :key="idx"
                :class="{
                  'green lighten-5 border-left-highlight':
                    isMyBusiness(res.name)
                }"
                class="mb-1 rounded"
              >

                <v-list-item-avatar
                  color="grey lighten-2"
                  size="30"
                >
                  <span class="font-weight-bold text-caption">
                    #{{ res.position || (idx + 1) }}
                  </span>
                </v-list-item-avatar>

                <v-list-item-content>

                  <v-list-item-title class="font-weight-bold">

                    {{ res.name }}

                    <v-chip
                      v-if="isMyBusiness(res.name)"
                      x-small
                      color="success"
                      class="ml-2"
                    >
                      YOUR BUSINESS
                    </v-chip>

                  </v-list-item-title>

                  <v-list-item-subtitle>
                    {{ res.rating }} ★
                    ({{ res.rating_count }} reviews)
                  </v-list-item-subtitle>

                </v-list-item-content>

              </v-list-item>

            </v-list>

          </v-card-text>

          <v-card-actions>

            <v-spacer></v-spacer>

            <v-tooltip bottom color="#434C5E">

              <template v-slot:activator="{ on, attrs }">

                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  color="primary"
                  text
                  @click="detailsDialog = false"
                >
                  Close
                </v-btn>

              </template>

              <span>
                Close the ranking results dialog.
              </span>

            </v-tooltip>

          </v-card-actions>

        </v-card>
      </v-dialog>

      <!-- Screenshot Preview Modal Dialog -->
      <v-dialog
        v-model="showScreenshotPreview"
        max-width="1000px"
        @click:outside="showScreenshotPreview = false"
      >

        <v-card v-if="previewScreenshotUrl">

          <v-card-title
            class="headline font-weight-bold d-flex justify-space-between align-center"
          >

            <span>
              Search Results Screenshot
            </span>

            <v-btn
              icon
              @click="showScreenshotPreview = false"
            >
              <v-icon>
                mdi-close
              </v-icon>
            </v-btn>

          </v-card-title>

          <v-card-text class="pa-0">

            <img
              :src="previewScreenshotUrl"
              alt="Search results screenshot"
              style="width: 100%; height: auto; display: block;"
            >

          </v-card-text>

        </v-card>
      </v-dialog>

    </div>
  </component>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import PanelNavigation from '@/components/PanelNavigation.vue'

export default {
  data() {
    return {
      loading: false,
      generatingRankings: false,
      emailReportDialog: false,
      sendingReport: false,
      reportEmail: '',
      company_name: '',
      agency_name: '',
      agency_logo: '',
      addAreaDialog: false,
      submittingAreas: false,
      newLocationsInput: '',
      tracker: null,
      mainSearches: [],
      gridPins: [],
      map: null,
      markers: [],
      detailsDialog: false,
      selectedSearch: null,
      showScreenshotPreview: false,
      previewScreenshotUrl: '',
      currentSearchIndex: -1,

      /*
       * Number of closest Rank Tracker points used
       * for the average rank calculation.
       *
       * Keep this configurable so it can be changed later
       * without rewriting the calculation.
       */
      closestPointsLimit: 5,

      rankColors: [
        '#15803d', // #1
        '#16a34a', // #2
        '#22c55e', // #3
        '#4ade80', // #4
        '#84cc16', // #5
        '#a3e635', // #6
        '#bef264', // #7
        '#facc15', // #8
        '#fbbf24', // #9
        '#f59e0b', // #10
        '#d97706', // #11
        '#f97316', // #12
        '#ea580c', // #13
        '#dc2626', // #14
        '#ef4444', // #15
        '#dc2626', // #16
        '#b91c1c', // #17
        '#991b1b', // #18
        '#881337', // #19
        '#7f1d1d'  // #20+
      ],
    };
  },

  components: {
    PanelNavigation
  },

  watch: {
    currentSearchIndex(newIndex) {
      if (
        newIndex >= 0 &&
        this.mainSearches[newIndex] &&
        this.map
      ) {
        this.renderMapForCurrentSearch();
      }
    }
  },

  computed: {
    isPublicReport() {
      return Boolean(this.$route.params.id);
    },

    rankGradient() {
      const colors = this.rankColors;

      const stops = colors.map((color, index) => {
        const percentage =
          (index / (colors.length - 1)) * 100;

        return `${color} ${percentage}%`;
      });

      return `linear-gradient(to right, ${stops.join(', ')})`;
    },

    /*
     * Returns the pins for the currently selected
     * Rank Tracker search/snapshot.
     */
    currentSearchPins() {
      const activeSearch =
        this.mainSearches[this.currentSearchIndex];

      if (
        activeSearch &&
        Array.isArray(activeSearch.grid_pins) &&
        activeSearch.grid_pins.length > 0
      ) {
        return activeSearch.grid_pins;
      }

      return this.gridPins;
    },

    /*
     * The new closest-point average.
     *
     * Process:
     *
     * 1. Find the main business location.
     * 2. Calculate distance from the main location
     *    to every tracked grid point.
     * 3. Sort closest -> furthest.
     * 4. Take the closest 5.
     * 5. Remove points without a valid rank.
     * 6. Average the remaining ranks.
     */
    closestRankStats() {
      const pins = this.currentSearchPins;

      if (!Array.isArray(pins) || pins.length === 0) {
        return {
          closestPoints: [],
          rankedPoints: [],
          averageRank: null,
          rankedCount: 0
        };
      }

      const mainPin =
        pins.find(pin => pin.is_main === true) ||
        this.gridPins.find(pin => pin.is_main === true);

      if (!mainPin) {
        return {
          closestPoints: [],
          rankedPoints: [],
          averageRank: null,
          rankedCount: 0
        };
      }

      const mainLat = Number(mainPin.lat);
      const mainLng = Number(mainPin.lng);

      if (
        !Number.isFinite(mainLat) ||
        !Number.isFinite(mainLng)
      ) {
        return {
          closestPoints: [],
          rankedPoints: [],
          averageRank: null,
          rankedCount: 0
        };
      }

      const pointsWithDistance = pins
        .filter(pin => {
          const lat = Number(pin.lat);
          const lng = Number(pin.lng);

          return (
            Number.isFinite(lat) &&
            Number.isFinite(lng)
          );
        })
        .map(pin => {
          const distance =
            this.calculateDistanceInMiles(
              mainLat,
              mainLng,
              Number(pin.lat),
              Number(pin.lng)
            );

          return {
            ...pin,
            distanceFromMain: distance
          };
        })
        .sort((a, b) => {
          /*
           * If two points have exactly the same distance,
           * use location name as a deterministic secondary sort.
           */
          if (
            a.distanceFromMain ===
            b.distanceFromMain
          ) {
            return String(a.location_name || '')
              .localeCompare(
                String(b.location_name || '')
              );
          }

          return (
            a.distanceFromMain -
            b.distanceFromMain
          );
        });

      const closestPoints =
        pointsWithDistance.slice(
          0,
          this.closestPointsLimit
        );

      const rankedPoints =
        closestPoints.filter(pin => {
          const rank = Number(pin.rank);

          return (
            Number.isFinite(rank) &&
            rank > 0
          );
        });

      if (rankedPoints.length === 0) {
        return {
          closestPoints,
          rankedPoints,
          averageRank: null,
          rankedCount: 0
        };
      }

      const totalRank =
        rankedPoints.reduce(
          (sum, pin) =>
            sum + Number(pin.rank),
          0
        );

      const averageRank =
        totalRank / rankedPoints.length;

      return {
        closestPoints,
        rankedPoints,
        averageRank: Number(
          averageRank.toFixed(1)
        ),
        rankedCount: rankedPoints.length
      };
    },

    rankingStats() {
      const ranks = this.mainSearches
        .filter(s => {
          const rank = Number(s.rank);

          return (
            Number.isFinite(rank) &&
            rank > 0
          );
        })
        .map(s => ({
          rank: Number(s.rank),
          date: this.formatDate(s.created_at)
        }));

      /*
       * Keep the old main-location metrics.
       */
      let bestRank = null;
      let worstRank = null;
      let bestDate = null;
      let worstDate = null;
      let avgMainRank = null;

      if (ranks.length > 0) {
        let best = ranks[0];
        let worst = ranks[0];
        let sum = 0;

        ranks.forEach(r => {
          sum += r.rank;

          if (r.rank < best.rank) {
            best = r;
          }

          if (r.rank > worst.rank) {
            worst = r;
          }
        });

        bestRank = best.rank;
        bestDate = best.date;
        worstRank = worst.rank;
        worstDate = worst.date;

        avgMainRank =
          Number(
            (sum / ranks.length).toFixed(1)
          );
      }

      /*
       * New closest-point average.
       */
      const closestAverage =
        this.closestRankStats.averageRank;

      return {
        bestRank,
        bestDate,
        worstRank,
        worstDate,

        /*
         * Kept for compatibility with any code
         * that may already reference avgRank.
         */
        avgRank: avgMainRank,

        /*
         * New metric.
         */
        closestPointsAverageRank:
          closestAverage,

        closestPointsCount:
          this.closestRankStats.rankedCount,

        closestPoints:
          this.closestRankStats.closestPoints
      };
    }
  },

  methods: {
    formatDate(date) {
      return moment(date).format(
        "MMM DD, YYYY hh:mm A"
      );
    },

    /*
     * Haversine distance calculation.
     *
     * Returns miles between two latitude/longitude points.
     */
    calculateDistanceInMiles(
      lat1,
      lng1,
      lat2,
      lng2
    ) {
      const earthRadiusMiles = 3958.7613;

      const toRadians = degrees =>
        degrees * (Math.PI / 180);

      const dLat =
        toRadians(lat2 - lat1);

      const dLng =
        toRadians(lng2 - lng1);

      const a =
        Math.sin(dLat / 2) *
          Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
          Math.cos(toRadians(lat2)) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);

      const c =
        2 *
        Math.atan2(
          Math.sqrt(a),
          Math.sqrt(1 - a)
        );

      return earthRadiusMiles * c;
    },

    /*
     * Returns the closest points with their
     * calculated distance.
     *
     * This is also useful if you later want to
     * highlight the 5 points on the map.
     */
    getClosestRankPoints() {
      return this.closestRankStats.closestPoints;
    },

    /*
     * Check whether a particular pin is one of
     * the points contributing to the average.
     */
    isClosestRankPoint(pin) {
      const closestPoints =
        this.closestRankStats.closestPoints;

      return closestPoints.some(point => {
        /*
         * Prefer a stable ID if one exists.
         */
        if (
          point.id !== undefined &&
          pin.id !== undefined
        ) {
          return point.id === pin.id;
        }

        /*
         * Fallback to coordinates.
         */
        return (
          Number(point.lat) === Number(pin.lat) &&
          Number(point.lng) === Number(pin.lng)
        );
      });
    },

    isMyBusiness(name) {
      if (!name || !this.tracker) {
        return false;
      }

      const target =
        this.tracker.business_name.toLowerCase();

      return name
        .toLowerCase()
        .includes(
          target
            .replace(', inc.', '')
            .trim()
        );
    },

    getRankColor(rank) {
      if (!rank || rank > 20) {
        return this.rankColors[19];
      }

      return this.rankColors[
        Math.min(rank - 1, 19)
      ];
    },

    getRankTextColor(rank) {
      return rank >= 5 && rank <= 10
        ? '#1f2937'
        : '#ffffff';
    },

    openDialog(search) {
      this.selectedSearch = search;
      this.detailsDialog = true;
    },

    previousSearch() {
      if (this.currentSearchIndex > 0) {
        this.currentSearchIndex--;
      }
    },

    nextSearch() {
      if (
        this.currentSearchIndex <
        this.mainSearches.length - 1
      ) {
        this.currentSearchIndex++;
      }
    },

    scrollToSearchOnMap(index) {
      this.currentSearchIndex = index;

      const mapElement =
        document.getElementById('map');

      if (mapElement) {
        mapElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    },

    getPinsForCurrentSearch() {
      return this.currentSearchPins;
    },

    renderMapForCurrentSearch() {
      if (
        !this.map ||
        !this.mainSearches[
          this.currentSearchIndex
        ]
      ) {
        return;
      }

      const currentSearch =
        this.mainSearches[
          this.currentSearchIndex
        ];

      const activePins =
        this.getPinsForCurrentSearch();

      /*
       * Clear existing markers.
       */
      this.markers.forEach(marker => {
        marker.setMap(null);
      });

      this.markers = [];

      const mainPin =
        activePins.find(
          p => p.is_main
        ) || activePins[0];

      if (mainPin) {
        this.map.panTo({
          lat: mainPin.lat,
          lng: mainPin.lng
        });
      }

      /*
       * Points used by the closest-point average.
       */
      const closestPoints =
        this.closestRankStats.closestPoints;

      activePins.forEach(pin => {
        const isMain =
          pin.is_main === true;

        const isZipCode =
          /\b\d{5}\b/.test(
            pin.location_name
          );

        const color =
          this.getRankColor(pin.rank);

        const labelText =
          pin.rank
            ? `#${pin.rank}`
            : '20+';

        const standardCityPath =
          "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z";

        const zipDiamondPath =
          "M12 1L23 12L12 23L1 12Z";

        const hqHexagonPath =
          "M12 1L21.5 6.5V17.5L12 23L2.5 17.5V6.5L12 1Z";

        let selectedPath =
          standardCityPath;

        let scaleFactor = 2.2;
        let strokeWidth = 1;
        let strokeColor = '#ffffff';

        if (isMain) {
          selectedPath =
            hqHexagonPath;

          scaleFactor = 2.8;
          strokeWidth = 3;
          strokeColor = '#1E293B';
        } else if (isZipCode) {
          selectedPath =
            zipDiamondPath;

          scaleFactor = 2;
          strokeWidth = 1.5;
          strokeColor = '#ffffff';
        }

        /*
         * Determine whether this point contributes
         * to the new closest-point average.
         */
        const isAveragePoint =
          closestPoints.some(point => {
            if (
              point.id !== undefined &&
              pin.id !== undefined
            ) {
              return point.id === pin.id;
            }

            return (
              Number(point.lat) === Number(pin.lat) &&
              Number(point.lng) === Number(pin.lng)
            );
          });

        const svgIcon = {
          path: selectedPath,
          fillColor: color,
          fillOpacity: 1,

          /*
           * Give points used in the average a
           * slightly stronger border.
           */
          strokeWeight:
            isAveragePoint && !isMain
              ? 3
              : strokeWidth,

          strokeColor:
            isAveragePoint && !isMain
              ? '#2563EB'
              : strokeColor,

          scale: scaleFactor,

          labelOrigin:
            new google.maps.Point(12, 12)
        };

        const marker =
          new google.maps.Marker({
            position: {
              lat: pin.lat,
              lng: pin.lng
            },

            map: this.map,

            icon: svgIcon,

            zIndex: isMain
              ? 9999
              : isAveragePoint
                ? 100
                : (isZipCode ? 5 : 10),

            label: {
              text: labelText,
              color:
                this.getRankTextColor(
                  pin.rank
                ),

              fontSize: isMain
                ? "14px"
                : (isZipCode
                  ? "11px"
                  : "12px"),

              fontWeight: "900"
            },

            title: isMain
              ? `${pin.location_name} (Main HQ)`
              : `${pin.location_name} ${
                  isZipCode
                    ? '(Zip Code)'
                    : '(City)'
                }`
          });

        /*
         * Open search detail dialog when
         * clicking a marker.
         */
        marker.addListener(
          'click',
          () => {
            const targetSearch =
              pin.latest_search ||
              (isMain
                ? currentSearch
                : null);

            if (targetSearch) {
              this.openDialog(
                targetSearch
              );
            }
          }
        );

        this.markers.push(marker);
      });
    },

    viewScreenshot(screenshotUrl) {
      this.previewScreenshotUrl =
        screenshotUrl;

      this.showScreenshotPreview = true;
    },

    async fetchData() {
      this.loading = true;

      try {
        const endpoint =
          this.isPublicReport
            ? '/rank-tracker/public/' +
              this.$route.params.id
            : '/rank-tracker/summary';

        const config =
          this.isPublicReport
            ? {}
            : {
                headers: {
                  'Authorization':
                    'Bearer ' +
                    this.$store.state.auth.jwt
                }
              };

        const res =
          await axios.get(
            process.env.VUE_APP_API_ENDPOINT +
              endpoint,
            config
          );

        this.tracker =
          res.data.tracker;

        this.mainSearches =
          res.data.main_searches;

        this.gridPins =
          res.data.grid_pins;

        if (
          this.mainSearches.length > 0
        ) {
          this.currentSearchIndex = 0;
        }

        if (res.data.user_email) {
          this.reportEmail =
            res.data.user_email;
        }

        if (res.data.company_name) {
          this.company_name =
            res.data.company_name;
        }

        if (res.data.agency_name) {
          this.agency_name =
            res.data.agency_name;
        }

        if (res.data.agency_logo) {
          this.agency_logo =
            res.data.agency_logo;
        }

        this.$nextTick(() => {
          this.initMap();
        });

      } catch (err) {
        console.error(
          "Failed to load Rank Tracker",
          err
        );
      } finally {
        this.loading = false;
      }
    },

    async sendReportEmail() {
      this.sendingReport = true;

      try {
        await axios.post(
          process.env.VUE_APP_API_ENDPOINT +
            '/rank-tracker/email-report',
          {
            email: this.reportEmail,

            report_url:
              window.location.origin +
              '/rank-tracker/' +
              this.tracker.user_id
          },
          {
            headers: {
              'Authorization':
                'Bearer ' +
                this.$store.state.auth.jwt
            }
          }
        );

        this.emailReportDialog =
          false;

      } catch (err) {
        console.error(
          "Failed to email rank tracker report",
          err
        );
      } finally {
        this.sendingReport = false;
      }

      this.$swal({
        title: "Sent",
        text: "Report successfully sent to the client!",
        icon: "success",
        showCancelButton: false,
      });
    },

    async generateRealTimeRankings() {
      this.generatingRankings = true;

      try {
        await axios.post(
          process.env.VUE_APP_API_ENDPOINT +
            '/rank-tracker/generate-real-time?' +
            new URLSearchParams({
              user_id:
                this.tracker.user_id
            }),
          {},
          {}
        );

        await this.fetchData();

      } catch (err) {
        console.error(
          "Failed to generate real-time rankings",
          err
        );
      } finally {
        this.generatingRankings = false;
      }
    },

    async submitNewServiceAreas() {
      if (
        !this.newLocationsInput.trim()
      ) {
        return;
      }

      this.submittingAreas = true;

      try {
        const res =
          await axios.post(
            process.env.VUE_APP_API_ENDPOINT +
              '/rank-tracker/add-service-areas',
            {
              locations:
                this.newLocationsInput
            },
            {
              headers: {
                'Authorization':
                  'Bearer ' +
                  this.$store.state.auth.jwt
              }
            }
          );

        if (res.data.success) {
          this.newLocationsInput = '';
          this.addAreaDialog = false;

          await this.fetchData();
        }

      } catch (err) {
        console.error(
          "Failed to add service areas",
          err
        );
      } finally {
        this.submittingAreas = false;
      }
    },

    initMap() {
      if (
        !window.google ||
        !document.getElementById('map')
      ) {
        return;
      }

      const mainPin =
        this.gridPins.find(
          p => p.is_main
        ) || this.gridPins[0];

      const center =
        mainPin
          ? {
              lat: mainPin.lat,
              lng: mainPin.lng
            }
          : {
              lat: 26.6933,
              lng: -80.0769
            };

      this.map =
        new google.maps.Map(
          document.getElementById('map'),
          {
            zoom: 10,
            center: center
          }
        );

      this.renderMapForCurrentSearch();
    }
  },

  mounted() {
    this.fetchData();
  }
};
</script>

<style scoped>
.report-brand-header {
  margin-bottom: 12px;
}

.report-agency-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
  margin-bottom: 6px;
}

.report-agency-logo {
  width: 34px;
  height: 34px;
  object-fit: contain;
  border-radius: 5px;
}

.report-agency-name {
  color: #276472;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.report-title {
  margin-bottom: 0 !important;
}

.rg-datatable {
  width: 100%;
  border-collapse: collapse;
}

.border-left-highlight {
  border-left: 4px solid #4CAF50 !important;
}

.border-success {
  border-left: 4px solid #4CAF50 !important;
}

.border-error {
  border-left: 4px solid #FF5252 !important;
}

.bg-disclaimer {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

/*
 * Makes the new average KPI visually identifiable
 * without changing the existing design significantly.
 */
.average-nearest-card {
  border-left: 4px solid #607D8B !important;
}

/* =========================================================
   MAP LEGEND
   ========================================================= */

.map-legend {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  padding: 16px 18px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

/* -------------------------
   Icon Legend
   ------------------------- */

.map-legend-icons {
  margin-bottom: 16px;
}

.map-legend-section-title {
  font-size: 13px;
  font-weight: 800;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.map-legend-items {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
}

.map-legend-item {
  display: flex;
  align-items: center;
  min-width: 180px;
  gap: 10px;
}

.map-legend-marker {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  overflow: visible;
}

.map-legend-marker path {
  fill: #22c55e;
  stroke: #ffffff;
  stroke-linejoin: round;
}

.map-legend-marker.marker-hq path {
  fill: #22c55e;
  stroke: #1e293b;
  stroke-width: 2.5;
}

.map-legend-marker.marker-city path {
  fill: #22c55e;
  stroke: #ffffff;
  stroke-width: 1;
}

.map-legend-marker.marker-zip path {
  fill: #22c55e;
  stroke: #ffffff;
  stroke-width: 1.5;
}

.map-legend-label {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  line-height: 1.2;
}

.map-legend-description {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

/* -------------------------
   Ranking Gradient
   ------------------------- */

.map-legend-ranking {
  border-top: 1px solid #e2e8f0;
  padding-top: 14px;
}

.map-legend-ranking-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.map-legend-ranking-subtitle {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.map-legend-ranking-note {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

.rank-gradient-wrapper {
  margin-top: 12px;
}

.rank-gradient {
  height: 12px;
  width: 100%;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.12);
}

.rank-gradient-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #475569;
}

.rank-gradient-description {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
  font-size: 10px;
  color: #94a3b8;
}

/* -------------------------
   Responsive
   ------------------------- */

@media (max-width: 700px) {
  .map-legend {
    padding: 14px;
  }

  .map-legend-items {
    gap: 12px;
  }

  .map-legend-item {
    min-width: 150px;
  }

  .map-legend-ranking-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .map-legend-ranking-note {
    white-space: normal;
  }
}
</style>